/**
 * Built-in Movie Studio templates. Art is generated in the compositor
 * (gradient cards and type), not copied from another editor's files.
 */

import type { TextClipStyle, TimelineClip, VideoProject } from '@canvas/shared-types';
import { insertClips, isClipProject, nextClipId, TRACK, withCanvas } from '@/lib/video/timelineEdit';

export interface EditorTemplate {
  id: string;
  category: string;
  title: string;
  blurb: string;
  durationMs: number;
  width: number;
  height: number;
  swatch: string;
  build: (atMs: number, groupId: string) => TimelineClip[];
}

function card(
  partial: Pick<TextClipStyle, 'text' | 'fontSize' | 'y' | 'fill'> &
    Partial<TextClipStyle> & { background?: string; gradientTo?: string; fullFrame?: boolean }
): TextClipStyle {
  return {
    fontFamily: 'Outfit, Arial, sans-serif',
    fontWeight: 700,
    align: 'center',
    x: 0.5,
    inPreset: 'rise',
    outPreset: 'fade-out',
    ...partial,
  };
}

function textClip(
  trackId: string,
  startMs: number,
  durationMs: number,
  name: string,
  groupId: string,
  text: TextClipStyle
): TimelineClip {
  return {
    id: nextClipId('tpl'),
    trackId,
    startMs,
    durationMs,
    name,
    groupId,
    kind: 'text',
    text,
  };
}

function pack(
  atMs: number,
  groupId: string,
  background: TextClipStyle,
  lines: Array<{ name: string; start: number; duration: number; text: TextClipStyle; track?: string }>
): TimelineClip[] {
  return [
    textClip(TRACK.visual, atMs, background.fontSize ? lines.reduce((m, line) => Math.max(m, line.start + line.duration), 0) : 8000, 'Background', groupId, background),
    ...lines.map((line) =>
      textClip(line.track || TRACK.textA, atMs + line.start, line.duration, line.name, groupId, line.text)
    ),
  ];
}

export const EDITOR_TEMPLATES: EditorTemplate[] = [
  {
    id: 'youtube-intro',
    category: 'YouTube',
    title: 'Welcome to my channel',
    blurb: 'Purple canyon intro',
    durationMs: 8000,
    width: 1920,
    height: 1080,
    swatch: 'linear-gradient(135deg,#2a1848,#6b3fa0)',
    build: (atMs, groupId) =>
      pack(
        atMs,
        groupId,
        card({
          text: '',
          fontSize: 64,
          y: 0.5,
          fill: '#fff',
          background: '#1b1030',
          gradientTo: '#6a3d9a',
          fullFrame: true,
          inPreset: 'none',
          outPreset: 'none',
        }),
        [
          {
            name: 'Welcome',
            start: 600,
            duration: 6400,
            text: card({ text: 'WELCOME TO MY CHANNEL', fontSize: 64, y: 0.46, fill: '#ffffff' }),
          },
          {
            name: 'Subscribe',
            start: 1800,
            duration: 4800,
            track: TRACK.textB,
            text: card({
              text: 'New videos every week',
              fontSize: 28,
              fontWeight: 500,
              y: 0.62,
              fill: '#e7d5ff',
              inPreset: 'fade-in',
            }),
          },
        ]
      ),
  },
  {
    id: 'topic-card',
    category: 'YouTube',
    title: 'Your topic explained',
    blurb: 'Title card for an explainer',
    durationMs: 5000,
    width: 1920,
    height: 1080,
    swatch: 'linear-gradient(135deg,#10243f,#1f6f8b)',
    build: (atMs, groupId) =>
      pack(atMs, groupId, card({
        text: '',
        fontSize: 48,
        y: 0.5,
        fill: '#fff',
        background: '#10243f',
        gradientTo: '#1f6f8b',
        fullFrame: true,
        inPreset: 'none',
        outPreset: 'none',
      }), [
        {
          name: 'Topic',
          start: 200,
          duration: 4600,
          text: card({ text: 'YOUR TOPIC\nEXPLAINED', fontSize: 72, y: 0.48, fill: '#f4f7fb', inPreset: 'pop-in' }),
        },
      ]),
  },
  {
    id: 'cooking-intro',
    category: 'YouTube',
    title: "Gourmet's kitchen",
    blurb: 'Cooking intro',
    durationMs: 6000,
    width: 1920,
    height: 1080,
    swatch: 'linear-gradient(135deg,#3a1d12,#c4622d)',
    build: (atMs, groupId) =>
      pack(atMs, groupId, card({
        text: '',
        fontSize: 48,
        y: 0.5,
        fill: '#fff',
        background: '#3a1d12',
        gradientTo: '#c4622d',
        fullFrame: true,
        inPreset: 'none',
        outPreset: 'none',
      }), [
        {
          name: 'Kitchen',
          start: 400,
          duration: 5200,
          text: card({ text: "Gourmet's Kitchen", fontSize: 68, y: 0.46, fill: '#fff6ea' }),
        },
        {
          name: 'With love',
          start: 1600,
          duration: 3800,
          track: TRACK.textB,
          text: card({ text: 'Cooking with love', fontSize: 28, fontWeight: 500, y: 0.62, fill: '#ffd7b0', inPreset: 'fade-in' }),
        },
      ]),
  },
  {
    id: 'cooking-outro',
    category: 'YouTube',
    title: 'Thanks for watching',
    blurb: 'Cooking outro',
    durationMs: 5000,
    width: 1920,
    height: 1080,
    swatch: 'linear-gradient(135deg,#f3b7b0,#fff1ea)',
    build: (atMs, groupId) =>
      pack(atMs, groupId, card({
        text: '',
        fontSize: 48,
        y: 0.5,
        fill: '#3a1d12',
        background: '#f6c7c1',
        gradientTo: '#fff4ee',
        fullFrame: true,
        inPreset: 'none',
        outPreset: 'none',
      }), [
        {
          name: 'Thanks',
          start: 300,
          duration: 4400,
          text: card({ text: 'Thanks for watching!', fontSize: 64, y: 0.46, fill: '#5a2430', inPreset: 'fade-in' }),
        },
        {
          name: 'Subscribe',
          start: 1400,
          duration: 3200,
          track: TRACK.textB,
          text: card({ text: 'Subscribe for the next recipe', fontSize: 26, fontWeight: 500, y: 0.62, fill: '#8a4450', inPreset: 'rise' }),
        },
      ]),
  },
];

export function editorTemplateById(id: string): EditorTemplate | null {
  return EDITOR_TEMPLATES.find((template) => template.id === id) ?? null;
}

/** Drop a template at the playhead. The first drop also sets the frame size. */
export function applyEditorTemplate(video: VideoProject, templateId: string, atMs: number): VideoProject {
  const template = editorTemplateById(templateId);
  if (!template || !isClipProject(video)) return video;
  const groupId = nextClipId('pack');
  const sized = video.clips.length === 0
    ? withCanvas(video, { width: template.width, height: template.height, bg: video.canvas.bg })
    : video;
  const duration = template.build(0, groupId).reduce((max, clip) => Math.max(max, clip.durationMs + (clip.startMs - atMs)), 0);
  const backgroundDuration = Math.max(template.durationMs, duration);
  const clips = template.build(atMs, groupId).map((clip) =>
    clip.name === 'Background' ? { ...clip, durationMs: backgroundDuration } : clip
  );
  return insertClips(sized, clips);
}
